import { useState, useMemo, useEffect, useCallback } from 'react'
import Fuse from 'fuse.js'

const STATUS_SCOPE_STORAGE_KEY = 'foxer-status-scope'
const DEFAULT_STATUS_SCOPE = 'all'

const isStatusScope = (value) => value === 'all' || value === 'todo' || value === 'done'

const normalizeStatusScope = (value) => (isStatusScope(value) ? value : DEFAULT_STATUS_SCOPE)

const getInitialStatusScope = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_STATUS_SCOPE
  }

  const storedScope = window.localStorage.getItem(STATUS_SCOPE_STORAGE_KEY)
  return normalizeStatusScope(storedScope)
}

export function useTodoFiltering(todos, selectedListId) {
  const [sortBy, setSortBy] = useState(null)
  const [filterBy, setFilterBy] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [statusScope, setStatusScope] = useState(getInitialStatusScope)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 200) // 200ms debounce
    return () => clearTimeout(handler)
  }, [searchQuery])

  const processedTodos = useMemo(() => {
    // 1. Filter by list
    let items = [...todos]
    if (selectedListId !== 'all') {
      items = items.filter((t) => t.listId === selectedListId)
    }

    // 1.5. Filter by global status scope
    if (statusScope !== 'all') {
      items = items.filter((t) => t.status === statusScope)
    }

    // 1.75. Filter by search query
    if (debouncedSearchQuery.trim()) {
      const fuse = new Fuse(items, {
        keys: ['text', 'description', 'subtasks.text'],
        threshold: 0.3,
      })
      items = fuse.search(debouncedSearchQuery).map((result) => result.item)
    }

    // 2. Filter by status/date
    if (filterBy) {
      const now = new Date()
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      const tomorrowStart = todayStart + 24 * 60 * 60 * 1000
      const dayAfterTomorrowStart = tomorrowStart + 24 * 60 * 60 * 1000
      items = items.filter((t) => {
        if (!t.startDate) return false
        const taskTime = new Date(t.startDate).getTime()
        if (filterBy === 'overdue') {
          return taskTime < todayStart && !t.completed
        } else if (filterBy === 'today') {
          return taskTime >= todayStart && taskTime < tomorrowStart
        } else {
          return taskTime >= tomorrowStart && taskTime < dayAfterTomorrowStart
        }
      })
    }

    // 3. Sort todos
    // Pinned items always come first unless explicitly sorted differently?
    // Actually, usually users want pinned at the top even within sections.
    items.sort((a, b) => {
      // Always prioritize pinned status
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1

      if (sortBy) {
        if (sortBy === 'priority') {
          const weight = {
            high: 3,
            medium: 2,
            low: 1,
          }
          return weight[b.priority] - weight[a.priority]
        }
        if (sortBy === 'date') {
          if (!a.startDate) return 1
          if (!b.startDate) return -1
          return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        }
      }
      return 0
    })
    return items
  }, [todos, selectedListId, statusScope, filterBy, sortBy, searchQuery])
  const kanbanStatuses = useMemo(() => {
    if (statusScope === 'todo') return ['todo']
    if (statusScope === 'done') return ['done']

    const statuses = Array.from(
      new Set(processedTodos.map((t) => t.status).concat(['todo', 'done'])),
    )
    statuses.sort((a, b) => {
      if (a === 'todo') return -1
      if (b === 'todo') return 1
      if (a === 'done') return 1
      if (b === 'done') return -1
      return a.localeCompare(b)
    })
    return statuses
  }, [processedTodos, statusScope])
  return {
    sortBy,
    setSortBy,
    filterBy,
    setFilterBy,
    statusScope,
    setStatusScope,
    searchQuery,
    setSearchQuery,
    processedTodos,
    kanbanStatuses,
  }
}
